import {Decimal} from 'decimal.js';
import {cloneDeep} from 'lodash';

export interface StyleConfig {
    strokeStyle: string;
    fillStyle: string;
    lineWidth: number;
    fontSizeInPx: number;
    fontFamily: string;
}

export interface StyleConfigWithPoint extends StyleConfig {
    pointStyle: string;
}

export interface PolygonSequence {
    start: DisguiseDto.MachineWorkingAreaBoundaryPolygonSegmentDto;
    end: DisguiseDto.MachineWorkingAreaBoundaryPolygonSegmentDto;
    between: DisguiseDto.MachineWorkingAreaBoundaryPolygonSegmentDto[];
}

class AmetrasMachineWorkingAreaBoundaryUtils {

    private static readonly DEFAULT_COORDS_LINE_WIDTH = 1;
    private static readonly DEFAULT_LINE_WIDTH = 3;
    private static readonly DEFAULT_FONT_SIZE = 20;
    private static readonly DEFAULT_CONFIG: DisguiseDto.MachineWorkingAreaBoundaryValueDto = {
        maxDistance: 0,
        polygonSegments: [
            {
                name: 'P0',
                distance: 0,
                angle: 0
            }, {
                name: 'P1',
                distance: 0,
                angle: 0
            }, {
                name: 'P2',
                distance: 0,
                angle: 0
            }, {
                name: 'P3',
                distance: 0,
                angle: 0
            }, {
                name: 'P4',
                distance: 0,
                angle: 0
            }, {
                name: 'P5',
                distance: 0,
                angle: 0
            }, {
                name: 'P6',
                distance: 0,
                angle: 0
            }, {
                name: 'P7',
                distance: 0,
                angle: 0
            }, {
                name: 'P8',
                distance: 0,
                angle: 0
            }, {
                name: 'P9',
                distance: 0,
                angle: 0
            },
            {
                name: 'P10',
                distance: 0,
                angle: 0
            }, {
                name: 'P11',
                distance: 0,
                angle: 0
            }, {
                name: 'P12',
                distance: 0,
                angle: 0
            }, {
                name: 'P13',
                distance: 0,
                angle: 0
            }, {
                name: 'P14',
                distance: 0,
                angle: 0
            }, {
                name: 'P15',
                distance: 0,
                angle: 0
            }, {
                name: 'P16',
                distance: 0,
                angle: 0
            }, {
                name: 'P17',
                distance: 0,
                angle: 0
            }, {
                name: 'P18',
                distance: 0,
                angle: 0
            }, {
                name: 'P19',
                distance: 0,
                angle: 0
            }
        ],
        circleSegments: [
            {
                name: 'S0',
                distance: 0,
                angle: 0
            }, {
                name: 'S1',
                distance: 0,
                angle: 0
            }, {
                name: 'S2',
                distance: 0,
                angle: 0
            }, {
                name: 'S3',
                distance: 0,
                angle: 0
            }, {
                name: 'S4',
                distance: 0,
                angle: 0
            }, {
                name: 'S5',
                distance: 0,
                angle: 0
            }
        ],
        squareSegments: [{
            name: 'V0',
            distance: 0,
            angle: 0
        }, {
            name: 'V1',
            distance: 0,
            angle: 0
        }, {
            name: 'V2',
            distance: 0,
            angle: 0
        }, {
            name: 'V3',
            distance: 0,
            angle: 0
        }]
    };

    private static readonly DEFAULT_STYLE_CONFIG: {
        polygons: StyleConfigWithPoint,
        circleSegments: StyleConfigWithPoint,
        square: StyleConfigWithPoint,
        coords: StyleConfig
    } = {
        polygons: {
            strokeStyle: '#388fc5',
            fillStyle: 'rgba(0,0,0,0.25)',
            pointStyle: 'rgba(0,0,0,0.5)',
            lineWidth: AmetrasMachineWorkingAreaBoundaryUtils.DEFAULT_LINE_WIDTH,
            fontFamily: 'serif',
            fontSizeInPx: AmetrasMachineWorkingAreaBoundaryUtils.DEFAULT_FONT_SIZE
        },
        circleSegments: {
            strokeStyle: '#388fc5',
            fillStyle: 'rgba(0,0,0,0.25)',
            pointStyle: 'rgba(0,0,0,0.5)',
            lineWidth: AmetrasMachineWorkingAreaBoundaryUtils.DEFAULT_LINE_WIDTH,
            fontFamily: 'serif',
            fontSizeInPx: AmetrasMachineWorkingAreaBoundaryUtils.DEFAULT_FONT_SIZE
        },
        square: {
            strokeStyle: '#388fc5',
            fillStyle: 'rgba(0,0,0,0.25)',
            pointStyle: 'rgba(0,0,0,0.5)',
            lineWidth: AmetrasMachineWorkingAreaBoundaryUtils.DEFAULT_LINE_WIDTH,
            fontFamily: 'serif',
            fontSizeInPx: AmetrasMachineWorkingAreaBoundaryUtils.DEFAULT_FONT_SIZE
        },
        coords: {
            strokeStyle: '#666',
            fillStyle: '#666',
            lineWidth: AmetrasMachineWorkingAreaBoundaryUtils.DEFAULT_COORDS_LINE_WIDTH,
            fontSizeInPx: AmetrasMachineWorkingAreaBoundaryUtils.DEFAULT_FONT_SIZE,
            fontFamily: 'serif'
        }
    };

    public getDefaultConfig(): DisguiseDto.MachineWorkingAreaBoundaryValueDto {
        return cloneDeep(AmetrasMachineWorkingAreaBoundaryUtils.DEFAULT_CONFIG);
    }

    public getMultipliedStyleConfig(multiplier: number): Partial<StyleConfig> {
        return {
            lineWidth: AmetrasMachineWorkingAreaBoundaryUtils.DEFAULT_LINE_WIDTH * multiplier,
            fontSizeInPx: AmetrasMachineWorkingAreaBoundaryUtils.DEFAULT_FONT_SIZE * multiplier
        };
    }

    public getMultipliedCoordsStyleConfig(multiplier: number): Partial<StyleConfig> {
        return {
            lineWidth: AmetrasMachineWorkingAreaBoundaryUtils.DEFAULT_COORDS_LINE_WIDTH * multiplier,
            fontSizeInPx: AmetrasMachineWorkingAreaBoundaryUtils.DEFAULT_FONT_SIZE * multiplier
        };
    }

    public drawPolygons(
        ctx: CanvasRenderingContext2D,
        polygons: DisguiseDto.MachineWorkingAreaBoundaryPolygonSegmentDto[],
        canvasSizeInPx: number,
        maxDistance: number,
        providedStyleConfig?: Partial<StyleConfigWithPoint>
    ) {
        const styleConfig = providedStyleConfig !== undefined ? {
            ...AmetrasMachineWorkingAreaBoundaryUtils.DEFAULT_STYLE_CONFIG.polygons,
            ...providedStyleConfig
        } : AmetrasMachineWorkingAreaBoundaryUtils.DEFAULT_STYLE_CONFIG.polygons;

        // 1. start is maxDistance
        // 2. end is maxDistance
        // 3. points in between MUST NOT be at maxDistance

        // 1. move to starting point
        // 2. draw circle
        // 3. line to end point
        // 4. line to all points in between
        // 5. closePath will move to the starting point (1)

        const polygonSequences: PolygonSequence[] = [];
        let tempSequence: Partial<PolygonSequence> = {
            between: []
        };
        for (const currentPolygon of polygons) {
            if ((currentPolygon.distance || 0) >= maxDistance) {
                if (!tempSequence.start) {
                    tempSequence.start = currentPolygon;
                } else {
                    tempSequence.end = currentPolygon;

                    if (tempSequence.start && tempSequence.end) {
                        polygonSequences.push({
                            start: tempSequence.start,
                            end: tempSequence.end,
                            between: tempSequence.between || []
                        });
                    }
                    tempSequence = {};
                }
            } else {
                if (!tempSequence.between) {
                    tempSequence.between = [];
                }
                tempSequence.between.push(currentPolygon);
            }
        }

        for (const currentPolygonSequence of polygonSequences) {
            ctx.beginPath();
            ctx.strokeStyle = styleConfig.strokeStyle;
            ctx.lineWidth = styleConfig.lineWidth;
            ctx.font = `${styleConfig.fontSizeInPx}px ${styleConfig.fontFamily}`;
            ctx.fillStyle = styleConfig.pointStyle;

            const startX = canvasSizeInPx / 2 + this.getX(currentPolygonSequence.start, canvasSizeInPx, maxDistance);
            const startY = canvasSizeInPx / 2 + this.getY(currentPolygonSequence.start, canvasSizeInPx, maxDistance);

            ctx.fillText(
                currentPolygonSequence.start.name || '',
                startX,
                startY
            );
            ctx.moveTo(
                startX,
                startY
            );

            ctx.arc(
                canvasSizeInPx / 2,
                canvasSizeInPx / 2,
                (canvasSizeInPx / 200) * 80,
                (currentPolygonSequence.start.angle || 0) * Math.PI / 180,
                (currentPolygonSequence.end.angle || 0) * Math.PI / 180,
            );

            const endX = canvasSizeInPx / 2 + this.getX(currentPolygonSequence.end, canvasSizeInPx, maxDistance);
            const endY = canvasSizeInPx / 2 + this.getY(currentPolygonSequence.end, canvasSizeInPx, maxDistance);
            ctx.fillText(
                currentPolygonSequence.end.name || '',
                endX,
                endY
            );
            ctx.lineTo(
                endX,
                endY
            );

            for (const currentBetweenPolygon of currentPolygonSequence.between) {
                const currentX = canvasSizeInPx / 2 + this.getX(currentBetweenPolygon, canvasSizeInPx, maxDistance);
                const currentY = canvasSizeInPx / 2 + this.getY(currentBetweenPolygon, canvasSizeInPx, maxDistance);
                ctx.fillText(
                    currentBetweenPolygon.name || '',
                    currentX,
                    currentY
                );
                ctx.lineTo(
                    currentX,
                    currentY
                );
            }

            ctx.closePath();
            ctx.stroke();
            ctx.fillStyle = styleConfig.fillStyle;
            ctx.fill();
            ctx.restore();
        }
        ctx.restore();
    }

    public drawCircleSegments(
        ctx: CanvasRenderingContext2D,
        circleSegments: DisguiseDto.MachineWorkingAreaBoundaryCircleSegmentDto[],
        canvasSizeInPx: number,
        maxDistance: number,
        providedStyleConfig?: Partial<StyleConfigWithPoint>
    ) {
        const styleConfig = providedStyleConfig !== undefined ? {
            ...AmetrasMachineWorkingAreaBoundaryUtils.DEFAULT_STYLE_CONFIG.circleSegments,
            ...providedStyleConfig
        } : AmetrasMachineWorkingAreaBoundaryUtils.DEFAULT_STYLE_CONFIG.circleSegments;

        ctx.strokeStyle = styleConfig.strokeStyle;
        ctx.lineWidth = styleConfig.lineWidth;
        ctx.font = `${styleConfig.fontSizeInPx}px ${styleConfig.fontFamily}`;

        for (let i = 0; i < circleSegments.length; i = i + 2) {
            const currentPoint = circleSegments[i];
            const nextPoint = circleSegments[i + 1];

            if ((currentPoint.distance || 0) > 0 && (nextPoint.distance || 0) > 0 && currentPoint.distance !== nextPoint.distance) {
                ctx.beginPath();
                ctx.fillStyle = styleConfig.pointStyle;

                const innerPoint = (currentPoint.angle || 0) < (nextPoint.angle || 0) ? currentPoint : nextPoint;
                const outerPoint = (currentPoint.angle || 0) > (nextPoint.angle || 0) ? currentPoint : nextPoint;

                const innerPointX = (canvasSizeInPx / 2) + AMETRAS_MACHINE_WORKING_AREA_BOUNDARY_UTILS_INSTANCE.getX(innerPoint, canvasSizeInPx, maxDistance);
                const innerPointY = (canvasSizeInPx / 2) + AMETRAS_MACHINE_WORKING_AREA_BOUNDARY_UTILS_INSTANCE.getY(innerPoint, canvasSizeInPx, maxDistance);
                ctx.fillText(
                    innerPoint.name || '',
                    innerPointX,
                    innerPointY
                );

                const outerPointX = (canvasSizeInPx / 2) + AMETRAS_MACHINE_WORKING_AREA_BOUNDARY_UTILS_INSTANCE.getX(outerPoint, canvasSizeInPx, maxDistance);
                const outerPointY = (canvasSizeInPx / 2) + AMETRAS_MACHINE_WORKING_AREA_BOUNDARY_UTILS_INSTANCE.getY(outerPoint, canvasSizeInPx, maxDistance);
                ctx.fillText(
                    outerPoint.name || '',
                    outerPointX,
                    outerPointY
                );
                ctx.arc(
                    (canvasSizeInPx / 2),
                    (canvasSizeInPx / 2),
                    new Decimal((innerPoint.distance || 0) * (80 / maxDistance)).mul(canvasSizeInPx / 200).toNumber(),
                    (outerPoint.angle || 0) * Math.PI / 180,
                    (innerPoint.angle || 0) * Math.PI / 180,
                    true
                );
                ctx.arc(
                    (canvasSizeInPx / 2),
                    (canvasSizeInPx / 2),
                    new Decimal((outerPoint.distance || 0) * (80 / maxDistance)).mul(canvasSizeInPx / 200).toNumber(),
                    (innerPoint.angle || 0) * Math.PI / 180,
                    (outerPoint.angle || 0) * Math.PI / 180
                );
                ctx.closePath();
                ctx.fillStyle = styleConfig.fillStyle;
                ctx.fill();
                ctx.stroke();
            }
        }
        ctx.restore();
    }

    public drawSquare(
        ctx: CanvasRenderingContext2D,
        squareSegment: DisguiseDto.MachineWorkingAreaBoundarySquareSegmentDto[],
        canvasSizeInPx: number,
        maxDistance: number,
        providedStyleConfig?: Partial<StyleConfigWithPoint>
    ) {
        const styleConfig = providedStyleConfig !== undefined ? {
            ...AmetrasMachineWorkingAreaBoundaryUtils.DEFAULT_STYLE_CONFIG.square,
            ...providedStyleConfig
        } : AmetrasMachineWorkingAreaBoundaryUtils.DEFAULT_STYLE_CONFIG.square;

        ctx.beginPath();
        ctx.strokeStyle = styleConfig.strokeStyle;
        ctx.fillStyle = styleConfig.pointStyle;
        ctx.lineWidth = styleConfig.lineWidth;
        ctx.font = `${styleConfig.fontSizeInPx}px ${styleConfig.fontFamily}`;

        let startedDrawing = false;
        for (const currentPoint of squareSegment) {
            if ((currentPoint.distance || 0) > 0) {
                const currentX = canvasSizeInPx / 2 + AMETRAS_MACHINE_WORKING_AREA_BOUNDARY_UTILS_INSTANCE.getX(currentPoint, canvasSizeInPx, maxDistance);
                const currentY = canvasSizeInPx / 2 + AMETRAS_MACHINE_WORKING_AREA_BOUNDARY_UTILS_INSTANCE.getY(currentPoint, canvasSizeInPx, maxDistance);

                if (!startedDrawing) {
                    startedDrawing = true;
                    ctx.fillText(
                        currentPoint.name || '',
                        currentX,
                        currentY
                    );
                    ctx.moveTo(
                        currentX,
                        currentY
                    );
                } else {
                    ctx.fillText(
                        currentPoint.name || '',
                        currentX,
                        currentY
                    );
                    ctx.lineTo(
                        currentX,
                        currentY
                    );
                }

            }
        }

        ctx.closePath();
        ctx.stroke();
        ctx.fillStyle = styleConfig.fillStyle;
        ctx.fill();
        ctx.restore();
    }

    public drawCoords(
        ctx: CanvasRenderingContext2D,
        canvasSizeInPx: number,
        maxDistance: number,
        providedStyleConfig?: Partial<StyleConfig>
    ) {
        const styleConfig = providedStyleConfig !== undefined ? {
            ...AmetrasMachineWorkingAreaBoundaryUtils.DEFAULT_STYLE_CONFIG.coords,
            ...providedStyleConfig
        } : AmetrasMachineWorkingAreaBoundaryUtils.DEFAULT_STYLE_CONFIG.coords;

        ctx.beginPath();
        ctx.strokeStyle = styleConfig.strokeStyle;
        ctx.lineWidth = styleConfig.lineWidth;
        ctx.font = `${styleConfig.fontSizeInPx}px ${styleConfig.fontFamily}`;
        ctx.fillStyle = styleConfig.fillStyle;

        // center
        ctx.arc(
            canvasSizeInPx / 2,
            canvasSizeInPx / 2,
            2,
            0,
            2 * Math.PI,
        );
        ctx.stroke();

        this.drawCoordCircle(
            ctx,
            canvasSizeInPx,
            maxDistance
        );

        ctx.fillText(
            '360°',
            canvasSizeInPx - ctx.measureText('360°').width,
            (canvasSizeInPx / 2) - (2 * canvasSizeInPx / 200)
        );
        ctx.fillText(
            '0°',
            canvasSizeInPx - ctx.measureText('0°').width,
            (canvasSizeInPx / 2) + styleConfig.fontSizeInPx
        );
        ctx.fillText(
            '180°',
            0,
            (canvasSizeInPx / 2) - (2 * canvasSizeInPx / 200)
        );
        ctx.fillText(
            '270°',
            (canvasSizeInPx / 2) - ctx.measureText('270°').width - (2 * canvasSizeInPx / 200),
            styleConfig.fontSizeInPx
        );
        ctx.fillText(
            '90°',
            (canvasSizeInPx / 2) + (2 * canvasSizeInPx / 200),
            canvasSizeInPx - (2 * canvasSizeInPx / 200)
        );

        // arrow east
        ctx.beginPath();
        ctx.moveTo(
            canvasSizeInPx,
            (canvasSizeInPx / 2)
        );
        ctx.lineTo(
            canvasSizeInPx - (5 * (canvasSizeInPx / 200)),
            (canvasSizeInPx / 2) - ((styleConfig.lineWidth / AmetrasMachineWorkingAreaBoundaryUtils.DEFAULT_COORDS_LINE_WIDTH) * (canvasSizeInPx / 200))
        );
        ctx.lineTo(
            canvasSizeInPx - (5 * (canvasSizeInPx / 200)),
            (canvasSizeInPx / 2) + ((styleConfig.lineWidth / AmetrasMachineWorkingAreaBoundaryUtils.DEFAULT_COORDS_LINE_WIDTH) * (canvasSizeInPx / 200))
        );
        ctx.closePath();
        ctx.fill();

        // arrow west
        ctx.beginPath();
        ctx.moveTo(
            0,
            (canvasSizeInPx / 2)
        );
        ctx.lineTo(
            (5 * (canvasSizeInPx / 200)),
            (canvasSizeInPx / 2) - ((styleConfig.lineWidth / AmetrasMachineWorkingAreaBoundaryUtils.DEFAULT_COORDS_LINE_WIDTH) * (canvasSizeInPx / 200))
        );
        ctx.lineTo(
            (5 * (canvasSizeInPx / 200)),
            (canvasSizeInPx / 2) + ((styleConfig.lineWidth / AmetrasMachineWorkingAreaBoundaryUtils.DEFAULT_COORDS_LINE_WIDTH) * (canvasSizeInPx / 200))
        );
        ctx.closePath();
        ctx.fill();

        // arrow north
        ctx.beginPath();
        ctx.moveTo(
            (canvasSizeInPx / 2),
            0
        );
        ctx.lineTo(
            (canvasSizeInPx / 2) + ((styleConfig.lineWidth / AmetrasMachineWorkingAreaBoundaryUtils.DEFAULT_COORDS_LINE_WIDTH) * (canvasSizeInPx / 200)),
            (5 * (canvasSizeInPx / 200))
        );
        ctx.lineTo(
            (canvasSizeInPx / 2) - ((styleConfig.lineWidth / AmetrasMachineWorkingAreaBoundaryUtils.DEFAULT_COORDS_LINE_WIDTH) * (canvasSizeInPx / 200)),
            (5 * (canvasSizeInPx / 200))
        );
        ctx.closePath();
        ctx.fill();

        // arrow south
        ctx.beginPath();
        ctx.moveTo(
            (canvasSizeInPx / 2),
            canvasSizeInPx
        );
        ctx.lineTo(
            (canvasSizeInPx / 2) + ((styleConfig.lineWidth / AmetrasMachineWorkingAreaBoundaryUtils.DEFAULT_COORDS_LINE_WIDTH) * (canvasSizeInPx / 200)),
            canvasSizeInPx - (5 * (canvasSizeInPx / 200))
        );
        ctx.lineTo(
            (canvasSizeInPx / 2) - ((styleConfig.lineWidth / AmetrasMachineWorkingAreaBoundaryUtils.DEFAULT_COORDS_LINE_WIDTH) * (canvasSizeInPx / 200)),
            canvasSizeInPx - (5 * (canvasSizeInPx / 200))
        );
        ctx.closePath();
        ctx.fill();

        // horizontal line
        ctx.beginPath();
        ctx.moveTo(
            (styleConfig.lineWidth / AmetrasMachineWorkingAreaBoundaryUtils.DEFAULT_COORDS_LINE_WIDTH),
            canvasSizeInPx / 2
        );
        ctx.lineTo(
            canvasSizeInPx - (styleConfig.lineWidth / AmetrasMachineWorkingAreaBoundaryUtils.DEFAULT_COORDS_LINE_WIDTH),
            canvasSizeInPx / 2
        );
        ctx.stroke();

        // vertical line
        ctx.beginPath();
        ctx.moveTo(
            canvasSizeInPx / 2,
            (styleConfig.lineWidth / AmetrasMachineWorkingAreaBoundaryUtils.DEFAULT_COORDS_LINE_WIDTH)
        );
        ctx.lineTo(
            canvasSizeInPx / 2,
            canvasSizeInPx - (styleConfig.lineWidth / AmetrasMachineWorkingAreaBoundaryUtils.DEFAULT_COORDS_LINE_WIDTH)
        );
        ctx.stroke();

        // diagonal lines
        ctx.beginPath();
        ctx.setLineDash([20, 20]);
        ctx.moveTo(
            0,
            0
        );
        ctx.lineTo(
            canvasSizeInPx,
            canvasSizeInPx
        );
        ctx.stroke();

        ctx.beginPath();
        ctx.moveTo(
            0,
            canvasSizeInPx
        );
        ctx.lineTo(
            canvasSizeInPx,
            0
        );
        ctx.stroke();
        ctx.restore();
    }

    private drawCoordCircle(
        ctx: CanvasRenderingContext2D,
        canvasSizeInPx: number,
        maxDistance: number
    ) {
        for (let circleOfEight = 1; circleOfEight <= 8; circleOfEight = circleOfEight + 1) {
            const distance = (maxDistance * circleOfEight / 8) * (80 / maxDistance);
            ctx.fillText(
                Number((maxDistance * circleOfEight / 8).toFixed(1)) + '',
                (canvasSizeInPx / 2),
                (canvasSizeInPx / 2) - ((canvasSizeInPx / 200) * distance)
            );
            ctx.beginPath();
            ctx.arc(
                canvasSizeInPx / 2,
                canvasSizeInPx / 2,
                (canvasSizeInPx / 200) * distance,
                0,
                2 * Math.PI,
            );
            ctx.stroke();
        }
    }


    private getX(point: DisguiseDto.MachineWorkingAreaBoundaryPointDto, canvasSizeInPx: number, maxDistance: number) {
        let angle = 0;
        const pointAngle = point.angle || 0;
        if (pointAngle > 0) {
            // convert degrees to einheitskreis degrees
            angle = 360 - pointAngle;
        }

        // convert given angle radian to degree
        const angleDecimal = new Decimal(angle * Math.PI / 180);
        const distanceDecimal = new Decimal((point.distance || 0) * (80 / maxDistance)).mul(canvasSizeInPx / 200);
        const angleCos = angleDecimal.cos().abs();
        let result = angleCos.mul(distanceDecimal);

        if (pointAngle < 270 && pointAngle > 90) {
            // west -> negate
            result = result.neg();
        }

        return result.toNumber();
    }

    private getY(point: DisguiseDto.MachineWorkingAreaBoundaryPointDto, canvasSizeInPx: number, maxDistance: number) {
        let angle = 0;
        const pointAngle = point.angle || 0;
        if (pointAngle > 0) {
            // convert degrees to einheitskreis degrees
            angle = 360 - pointAngle;
        }

        // convert given angle radian to degree
        const angleDecimal = new Decimal(angle * Math.PI / 180);
        const distanceDecimal = new Decimal((point.distance || 0) * (80 / maxDistance)).mul(canvasSizeInPx / 200);
        const angleSin = angleDecimal.sin().abs();
        let result = angleSin.mul(distanceDecimal);

        if (pointAngle > 180) {
            // north -> negate
            result = result.neg();
        }

        return result.toNumber();
    }
}

const AMETRAS_MACHINE_WORKING_AREA_BOUNDARY_UTILS_INSTANCE = new AmetrasMachineWorkingAreaBoundaryUtils();

export {AMETRAS_MACHINE_WORKING_AREA_BOUNDARY_UTILS_INSTANCE, };
